import { useState, useMemo, useEffect } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { Link } from "react-router-dom";
import OpenMatchList from "@/components/activities/ActivityList";
import OpenMatchFilters from "@/components/activities/ActivityFilters";
import ActivityFilterSidebar from "@/components/activity/ActivityFilterSidebar";
import ResponsiveLayout from "@/components/layout/ResponsiveLayout";
import Leagues from "@/pages/Leagues";
import Tournaments from "@/pages/Tournaments";
import Training from "@/pages/Training";
import ActiveMatchCard from "@/components/tournaments/ActiveMatchCard";
// Spår 1b: OpenMatchesEmbed (V2 OpenMatches.tsx) raderad. "open-matches"-tab
// borttagen från Play.tsx tabs-listan; V1 OpenMatchList visas i "all"-tab.
import { useTranslation } from "react-i18next";

export default function Play() {
  const { t } = useTranslation('activities');
  const { isEnabled } = useFeatureFlags();
  const { types: activityTypes } = useActivityTypes();

  const [dateFilter, setDateFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [onlyJoinable, setOnlyJoinable] = useState(false);
  const [matchMyLevel, setMatchMyLevel] = useState(false);

  const openMatchesEnabled = isEnabled("player_activities") || isEnabled("open_matches");
  const leaguesEnabled = isEnabled("leagues");
  const tournamentsEnabled = isEnabled("tournaments");
  const trainingEnabled = isEnabled("training");

  const tabs = useMemo(() => {
    const tabItems: { value: string; label: string }[] = [];
    if (openMatchesEnabled) tabItems.push({ value: "all", label: t('common:all') });
    // Spår 1b: "open-matches"-tab (V2 OpenMatches-embed) borttagen — V1
    // OpenMatchList visas i "all"-tab. Tab kan återinföras i Spår 3.
    if (leaguesEnabled) tabItems.push({ value: "leagues", label: t('leaguesTab') });
    if (tournamentsEnabled) tabItems.push({ value: "tournaments", label: t('tournaments') });
    if (trainingEnabled) tabItems.push({ value: "training", label: t('trainingTab') });
    return tabItems;
  }, [openMatchesEnabled, leaguesEnabled, tournamentsEnabled, trainingEnabled, t]);

  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.value === activeTab)) {
      setActiveTab(tabs[0].value);
    }
  }, [tabs, activeTab]);

  if (tabs.length === 0) {
    return (
      <div className="space-y-4 p-4">
        <h1 className="text-xl font-bold">{t('findPlay')}</h1>
        <p className="text-muted-foreground">{t('noActivitiesAvailable')}</p>
      </div>
    );
  }

  // Desktop sidebar content (only shown on lg+)
  const sidebarContent = (
    <ActivityFilterSidebar
      typeFilter={typeFilter}
      onTypeChange={setTypeFilter}
      onlyJoinable={onlyJoinable}
      onOnlyJoinableChange={setOnlyJoinable}
      matchMyLevel={matchMyLevel}
      onMatchMyLevelChange={setMatchMyLevel}
      activityTypes={activityTypes}
    />
  );

  const mainContent = (
    <div className="space-y-4">
      <ActiveMatchCard />

      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('findPlay')}</h1>
        {/* T2v2 (R-4): ONE consistent create entry (mobile + desktop) → the
            canonical wizard. Replaces the confusing "Skapa via Boka" link. */}
        <Button size="sm" asChild className="gap-1">
          <Link to="/create-activity">
            <Plus className="h-3.5 w-3.5" /> {t('createActivity')}
          </Link>
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {tabs.length > 1 && (
          <TabsList className="w-full overflow-x-auto">
            {tabs.map(tab => (
              <TabsTrigger key={tab.value} value={tab.value} className="flex-1 text-xs">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        {openMatchesEnabled && (
          <TabsContent value="all" className="space-y-3 mt-3">
            {/* Mobile: inline filters; Desktop: hidden (sidebar used instead) */}
            <div className="lg:hidden">
              <OpenMatchFilters
                dateFilter={dateFilter}
                onDateChange={setDateFilter}
                typeFilter={typeFilter}
                onTypeChange={setTypeFilter}
                onlyJoinable={onlyJoinable}
                onOnlyJoinableChange={setOnlyJoinable}
                matchMyLevel={matchMyLevel}
                onMatchMyLevelChange={setMatchMyLevel}
                activityTypes={activityTypes}
              />
            </div>

            {/* Desktop: date strip only (type/toggle filters are in sidebar) */}
            <div className="hidden lg:block">
              <OpenMatchFilters
                dateFilter={dateFilter}
                onDateChange={setDateFilter}
                typeFilter={typeFilter}
                onTypeChange={setTypeFilter}
                onlyJoinable={onlyJoinable}
                onOnlyJoinableChange={setOnlyJoinable}
                matchMyLevel={matchMyLevel}
                onMatchMyLevelChange={setMatchMyLevel}
                activityTypes={activityTypes}
                hideTypeFilter
                hideToggles
              />
            </div>

            <OpenMatchList
              dateFilter={dateFilter || undefined}
              levelFilter={matchMyLevel}
              typeFilter={typeFilter !== "all" ? typeFilter : undefined}
              onlyJoinable={onlyJoinable}
            />

            <p className="text-center text-xs text-muted-foreground pt-4">
              {t('cantFindAnything')}{" "}
              <Link to="/create-activity" className="text-primary hover:underline">
                {t('createActivity')}
              </Link>
            </p>
          </TabsContent>
        )}

        {/* Spår 1b: "open-matches"-TabsContent (V2 OpenMatchesEmbed) rivet. */}

        {leaguesEnabled && (
          <TabsContent value="leagues" className="mt-3"><Leagues /></TabsContent>
        )}
        {tournamentsEnabled && (
          <TabsContent value="tournaments" className="mt-3 space-y-4">
            <Tournaments embedded />
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {t('tournamentsActivitiesHeading')}
              </h3>
              <OpenMatchList typeFilterIn={["americano", "vinnarbana", "mexicano"]} />
            </div>
          </TabsContent>
        )}
        {trainingEnabled && (
          <TabsContent value="training" className="mt-3"><Training /></TabsContent>
        )}
      </Tabs>
    </div>
  );

  return (
    <ResponsiveLayout
      main={mainContent}
      sidebar={sidebarContent}
      sidebarPosition="left"
      sidebarWidth="w-64"
    />
  );
}
